import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { InboundContext } from '../../../context/InboundContext';
import { createInboundApplication } from '../../../services/inbound.service';
import { Button, Heading, Text } from '../../styledComponents';
import styles from './FormContainer.module.css';
import {
  PersonalInformation,
  SocialMediaLinks,
  AmbassadorProgramQuestions,
  AddressDetails,
  PaymentDetails,
  OtherDetails,
} from './FormSections';
import ProductSelect from './FormSections/ProductSelect';
import { stripeURL } from '../../../utils/global';
import { getAddressLineOneError, isAddressLineOneValid } from '../../../utils/validations';

export default function FormContainer() {
  const {
    creatorEmail,
    inboundData,
    applicationId,
    t,
    subDomainSuffix,
    formId,
    influencerAddress,
    setInfluencerAddress,
  } = useContext(InboundContext);

  const [personalInformation, setPersonalInformation] = useState({ firstName: '', lastName: '', email: creatorEmail });
  const [socialMediaLinks, setSocialMediaLinks] = useState({
    youtube: '',
    instagram: '',
    tiktok: '',
    facebook: '',
    twitter: '',
    blog: '',
  });
  const [ambassadorQuestions, setAmbassadorQuestions] = useState<any>(
    inboundData?.formSetupPage.ambassadorProgramQuestions.map((que) => ({ question: que.question, answer: '' }))
  );
  const [paymentDetails, setPaymentDetails] = useState({
    paymentType: inboundData?.formSetupPage?.allowedPaymentMethods?.[0] || 'paypal',
    paymentEmail: '',
  });
  const [customMessage, setCustomMessage] = useState('');
  const [errorMessage, setErrormessage] = useState('');
  // const [options, setOptions] = useState<any>({});
  // const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [selectedProducts, setSelectedProducts] = useState<any>([]);

  const navigate = useNavigate();

  // const getVariantDetails = (id: any, variants: any) => {
  //   if (variants?.[0]?.option1 === 'Default Title') {
  //     return variants[0];
  //   }
  //   const option = options[id];
  //   const selectedVariant = variants?.find((product: any) => {
  //     if (product?.option1 && product?.option2 && product?.option3) {
  //       return (
  //         product?.option1 === option?.option1 &&
  //         product?.option2 === option?.option2 &&
  //         product?.option3 === option?.option3
  //       );
  //     }
  //     if (product?.option1 && product?.option2) {
  //       return product?.option1 === option?.option1 && product?.option2 === option?.option2;
  //     }
  //     if (product?.option1 === option?.option1) {
  //       return product?.option1 === option?.option1;
  //     }
  //     return null;
  //   });
  //   return selectedVariant;
  // };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();
    setErrormessage('');

    let instagramUrl = stripeURL(socialMediaLinks.instagram.replaceAll('@', ''));
    // Max Username character limit
    instagramUrl = instagramUrl.slice(0, 30);
    const tiktokUrl = stripeURL(socialMediaLinks.tiktok);
    let twitterUrl = stripeURL(socialMediaLinks.twitter);

    twitterUrl = twitterUrl.slice(0, 15);
    let youtubeUrl = stripeURL(socialMediaLinks.youtube);
    youtubeUrl = youtubeUrl.slice(0, 20);
    let facebookUrl = stripeURL(socialMediaLinks.facebook);
    facebookUrl = facebookUrl.slice(0, 50);
    if (inboundData?.formSetupPage?.giftOption?.isActive) {
      if (!selectedProducts.length) {
        setErrormessage('Please select a product');
        return;
      }
    }

    if (!instagramUrl && !tiktokUrl && !youtubeUrl) {
      setErrormessage('At least one social media link is compulsory');
      return;
    }

    if (inboundData?.formSetupPage?.address) {
      if (!influencerAddress.country) {
        setErrormessage('Country is required');
        return;
      }
      if (!isAddressLineOneValid(influencerAddress.addressLineOne)) {
        setErrormessage(getAddressLineOneError(influencerAddress.addressLineOne));
        return;
      }
    }

    const giftProductInfo = selectedProducts.map((product: any) => ({
      productId: product.productId,
      variantId: product.variantId,
      title: product.title,
      price: product.price,
      quantity: 1,
      productImage: product?.productImage,
      variantDetails: product?.variantDetails,
    }));

    const body = {
      applicationId,
      application: {
        email: personalInformation.email,
        firstName: personalInformation.firstName,
        lastName: personalInformation.lastName,
        socialLinks: {
          instagram: instagramUrl ? `https://instagram.com/${instagramUrl}` : '',
          tiktok: tiktokUrl ? `https://tiktok.com/${tiktokUrl[0] !== '@' ? `@${tiktokUrl}` : tiktokUrl}` : '',
          twitter: twitterUrl ? `https://twitter.com/${twitterUrl}` : '',
          youtube: youtubeUrl ? `https://youtube.com/${youtubeUrl}` : '',
          facebook: facebookUrl ? `https://facebook.com/${facebookUrl}` : '',
          blog: socialMediaLinks?.blog ? socialMediaLinks?.blog : '',
        },
        ambassadorProgramQuestions: ambassadorQuestions.map((que: { question: string; answer: string }) => ({
          q: que.question,
          a: que.answer,
        })),
        address: {
          addressLineOne: influencerAddress.addressLineOne,
          addressLineTwo: influencerAddress.addressLineTwo,
          pincode: influencerAddress.pincode,
          city: influencerAddress.city,
          state: influencerAddress.state,
          country: influencerAddress.country,
          phone: influencerAddress.phone,
        },
        paymentDetails: {
          paymentType: paymentDetails.paymentType.toLowerCase(),
          paymentEmail: paymentDetails.paymentEmail,
        },
        customMessage,
        giftProductInfo,
      },
      formId,
    };

    const response = await createInboundApplication(body);
    if (response.success) {
      navigate(`/${subDomainSuffix}/success`, { replace: true });
    } else {
      setErrormessage(response.msg);
    }
  };

  useEffect(() => {
    if (!creatorEmail) {
      navigate(`/${subDomainSuffix}`);
    }
  }, [creatorEmail, navigate, subDomainSuffix]);

  return (
    <div className={styles.formContainer}>
      <div className={styles.formTitle}>
        <Heading as="h3" fontWeight="500" fontSize="20px" color="#353535">
          {t('formHeader')}
        </Heading>
      </div>

      <form onSubmit={handleFormSubmit}>
        <PersonalInformation
          personalInformation={personalInformation}
          setPersonalInformation={setPersonalInformation}
        />
        <SocialMediaLinks socialMediaLinks={socialMediaLinks} setSocialMediaLinks={setSocialMediaLinks} />
        <AmbassadorProgramQuestions
          ambassadorQuestions={ambassadorQuestions}
          setAmbassadorQuestions={setAmbassadorQuestions}
        />
        <ProductSelect
          selectedProducts={selectedProducts}
          setSelectedProducts={setSelectedProducts}
          formId={formId || ''}
        />
        <AddressDetails address={influencerAddress} setAddress={setInfluencerAddress} />
        <PaymentDetails paymentDetails={paymentDetails} setPaymentDetails={setPaymentDetails} />
        <OtherDetails customMessage={customMessage} setCustomMessage={setCustomMessage} />

        {errorMessage && (
          <div
            className={styles.formSection}
            style={{ borderBottom: 'none', paddingBottom: 0, marginBottom: -20, background: 'transparent' }}
          >
            <Text color="var(--red)">{errorMessage}</Text>
          </div>
        )}
        <div className={styles.submitButtonContainer}>
          <Button
            bgColor={inboundData?.formSetupPage.colors.btn}
            color={inboundData?.formSetupPage.colors.btnText}
            type="submit"
          >
            {t('submitApplicationCta')}
          </Button>
        </div>
      </form>
    </div>
  );
}
